import { Controller } from "@hotwired/stimulus";
import markerSDK from "@marker.io/browser";

// Marker.io's UAT feedback widget. The project id and the reporter /
// customData that label each report come from Rails via
// Components::MarkerWidget — nothing about the tester is hardcoded here.
//
// Two deliberate departures from the house third-party-library rules
// (STIMULUS_CONVENTIONS.md → Third-party libraries, Lifecycle & teardown),
// both forced by the fact that Marker mounts itself onto document.body
// while Turbo replaces document.body on every visit:
//
// 1. Static import rather than a lazy `import()`. `@marker.io/browser` is a
//    ~1KB loader whose whole job is to inject edge.marker.io's shim; the
//    weight is in the shim, fetched at runtime either way, so deferring the
//    loader buys nothing. It also closes a race — `loadWidget` assigns
//    `window.Marker` synchronously, so a Turbo visit landing mid-load can
//    never start a second widget.
//
// 2. The widget is re-loaded on every connect rather than mounted once. The
//    navigation itself destroys it, so there is nothing to preserve and
//    nothing to tear down in `disconnect`; what the next page needs is a
//    fresh one. `data-turbo-permanent` can't save it either — it preserves
//    elements the server rendered, and Marker's root is a sibling it
//    appends after load.
export default class extends Controller {
  static values = { project: String, reporter: Object, customData: Object };

  connect() {
    // Turbo's cached preview render: the real body is milliseconds away and
    // would stack a second widget on top of this one.
    if (document.documentElement.hasAttribute("data-turbo-preview")) return;

    this.retireStaleWidget();

    const params = { project: this.projectValue, customData: this.customDataValue };
    if (this.hasReporterValue) params.reporter = this.reporterValue;

    // loadWidget can throw synchronously as well as reject, and an
    // uncaught throw here would abort Stimulus's connect for the element.
    try {
      markerSDK.loadWidget(params).catch((error) => this.warn(error));
    } catch (error) {
      this.warn(error);
    }
  }

  // The Turbo visit that brought us here took the previous widget's root
  // and its injected <style> down with the old body. Marker's own unload()
  // then throws on the style element it can no longer find — and since
  // loadWidget calls unload() itself whenever window.Marker is set, that
  // throw comes straight back out of connect() and the replacement widget
  // never loads: the tester silently loses the Report button from their
  // second page onwards.
  //
  // So retire the corpse here instead. unload() is still worth attempting —
  // it disposes the old view instance before it trips — and dropping the
  // global afterwards puts loadWidget back on its clean first-load path.
  retireStaleWidget() {
    if (!window.Marker) return;

    try {
      window.Marker.unload();
    } catch {
      // Half-dismantled by the navigation already; nothing left to recover.
    }
    delete window.Marker;
  }

  // An ad blocker, a locked-down pharmacy network or an offline fridge room
  // must not take the page down with it. The widget is a tester
  // convenience; the app is the product.
  warn(error) {
    console.warn("(Marker.io) feedback widget failed to load", error);
  }
}
